import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import BookingModal from "@/components/BookingModal";
import { IMAGES, CERTS } from "@/config";

export default function AboutPage() {
  const { t } = useLanguage();
  const [bookingOpen, setBookingOpen] = useState(false);
  return (
    <main className="pt-28 pb-20" data-testid="about-page">
      <Seo page="about" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div className="relative">
                <img src={IMAGES.about} alt={t.about.title} className="rounded-3xl object-cover w-full h-[520px] shadow-2xl" data-testid="about-portrait" />
                <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-terra text-white rounded-2xl shadow-xl px-6 py-4">
                  <p className="font-serif text-xl font-semibold">30+</p>
                  <p className="text-xs opacity-80">{t.hero.sub.split("·")[0].trim().replace(/^\d+\+\s*/, "")}</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-4">{t.about.eyebrow}</p>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-2" data-testid="about-title">{t.about.title}</h1>
              <p className="text-sm font-mono uppercase tracking-[0.14em] text-sage mb-8" data-testid="about-role">{t.about.role}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base sm:text-lg text-muted2 leading-relaxed mb-6">{t.about.p1}</p>
              <p className="text-base sm:text-lg text-muted2 leading-relaxed mb-6">{t.about.p2}</p>
              <p className="text-base sm:text-lg text-muted2 leading-relaxed mb-10">{t.about.p3}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote className="border-l-2 border-terra/60 pl-6 mb-10" data-testid="about-thought">
                <p className="font-serif text-2xl sm:text-3xl italic leading-snug">{t.about.thought}</p>
                <p className="text-sm text-muted2 leading-relaxed mt-3">{t.about.thought2}</p>
              </blockquote>
            </Reveal>
            <Reveal delay={0.2}>
              <h2 className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-6">{t.about.creds_title}</h2>
              <div className="relative border-l border-terra/30 pl-8 space-y-7 mb-12" data-testid="about-credentials">
                {t.about.timeline.map((m, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-[37px] top-2 w-2.5 h-2.5 rounded-full bg-terra" />
                    <p className="font-serif text-2xl text-sage font-semibold leading-none">{m.y}</p>
                    <p className="text-sm sm:text-base text-ink/85 mt-1.5">{m.t}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <h2 className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-6">{t.about.certs_title}</h2>
              <div className="flex flex-wrap items-start gap-5 mb-12" data-testid="about-certs">
                <div className="bg-white rounded-2xl border border-line p-3 shadow-sm shrink-0">
                  <img src={CERTS.badge} alt="CFMP — Functional Medicine Practitioner" className="w-20 h-20 object-contain" data-testid="cfmp-badge" />
                </div>
                <div className="flex-1 min-w-[260px] space-y-2">
                  {CERTS.items.map((c, i) => (
                    <div key={c.name} className="flex items-center justify-between gap-4 bg-white rounded-xl border border-line px-5 py-3" data-testid={`cert-item-${i}`}>
                      <div>
                        <p className="text-sm font-semibold">{c.name}</p>
                        <p className="text-xs text-muted2">{c.org}</p>
                      </div>
                      {c.file && (
                        <a href={c.file} target="_blank" rel="noreferrer" className="text-xs font-semibold text-terra hover:text-sage whitespace-nowrap transition-colors" data-testid={`cert-view-${i}`}>
                          {t.about.certs_view}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <button onClick={() => setBookingOpen(true)} data-testid="booking-cta-about" className="group inline-flex items-center gap-2 rounded-full bg-sage px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-ink hover:shadow-xl hover:-translate-y-0.5">
                {t.hero.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
    </main>
  );
}
